import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

interface UploadTask {
    id: string; // Typically the entity ID (e.g., seed ID)
    fileName: string;
    progress: number;
    status: 'uploading' | 'completed' | 'error';
    error?: string;
}

interface UploadContextType {
    activeUploads: UploadTask[];
    uploadFile: (id: string, path: string, file: File, onComplete: (imageUrl: string, storagePath: string) => Promise<void>) => Promise<void>;
    isUploading: (id: string) => boolean;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeUploads, setActiveUploads] = useState<UploadTask[]>([]);

    const isUploading = useCallback((id: string) => {
        return activeUploads.some(u => u.id === id && u.status === 'uploading');
    }, [activeUploads]);

    const uploadFile = useCallback(async (
        id: string,
        path: string,
        file: File,
        onComplete: (imageUrl: string, storagePath: string) => Promise<void>
    ) => {
        const uploadTask: UploadTask = {
            id,
            fileName: file.name,
            progress: 0,
            status: 'uploading',
        };

        setActiveUploads(prev => [...prev, uploadTask]);

        try {
            const fileRef = ref(storage, path);
            const uploadResult = await uploadBytes(fileRef, file);
            const storagePath = uploadResult.metadata.fullPath;
            const imageUrl = await getDownloadURL(fileRef);

            await onComplete(imageUrl, storagePath);

            setActiveUploads(prev => prev.filter(u => u.id !== id));
        } catch (error: any) {
            console.error('Upload failed:', error);
            setActiveUploads(prev => prev.map(u =>
                u.id === id ? { ...u, status: 'error', error: error.message } : u
            ));
            // Keep error in state for a while or until cleared?
            // For now, let's just alert
            alert(`Failed to upload ${file.name}: ${error.message}`);
            setTimeout(() => {
                setActiveUploads(prev => prev.filter(u => u.id !== id));
            }, 5000);
        }
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (activeUploads.length > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [activeUploads]);

    return (
        <UploadContext.Provider value={{ activeUploads, uploadFile, isUploading }}>
            {children}
        </UploadContext.Provider>
    );
};

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
};
